import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Calendar as CalIcon, Search, Loader2, Plus, Pencil, Trash2,
  ChevronLeft, AlertCircle, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import EventForm from "../components/calendar/EventForm";
import { toast } from "sonner";

const TYPE_LABEL = {
  shiur:    "שיעור",
  yahrzeit: "יארצייט",
  birthday: "יום הולדת",
  reminder: "תזכורת",
};

const RECURRENCE_LABEL = {
  none:             "חד-פעמי",
  yearly_hebrew:    "כל שנה (עברי)",
  yearly_gregorian: "כל שנה (לועזי)",
  weekly:           "כל שבוע",
};

const COLOR_DOT = {
  gold:   "bg-gold/80",
  navy:   "bg-[#1E2A3A]",
  green:  "bg-emerald-600",
  red:    "bg-rose-600",
  purple: "bg-violet-600",
};

const FILTERS = [
  { id: "all",      label: "הכל" },
  { id: "shiur",    label: "שיעור" },
  { id: "yahrzeit", label: "יארצייט" },
  { id: "birthday", label: "יום הולדת" },
  { id: "reminder", label: "תזכורת" },
];

function daysFromNow(iso) {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / (24 * 3600 * 1000));
}

function relativeLabel(diff) {
  if (diff === null) return "";
  if (diff === 0) return "היום";
  if (diff === 1) return "מחר";
  if (diff > 0)   return `בעוד ${diff} ימים`;
  if (diff === -1) return "אתמול";
  return `לפני ${-diff} ימים`;
}


export default function MyEvents() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // The list view shows the next upcoming occurrence per stored event.
  // We query a generous +2 year window so weekly + yearly recurrences
  // surface their next instance even when the event itself was added
  // long ago.
  const fromTo = useMemo(() => {
    const today = new Date();
    const from = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const future = new Date(today.getFullYear() + 2, today.getMonth(), today.getDate());
    const to = `${future.getFullYear()}-${String(future.getMonth() + 1).padStart(2, "0")}-${String(future.getDate()).padStart(2, "0")}`;
    return { from, to };
  }, []);

  const fetchAll = () => {
    setLoading(true);
    api.myEventsList(fromTo)
      .then((d) => {
        setItems(d.items || []);
        setOccurrences(d.occurrences || []);
      })
      .catch(() => { setItems([]); setOccurrences([]); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [fromTo.from]);

  // Build a per-event "next occurrence" map so a single recurring
  // event renders as one row showing its closest future date.
  const nextByEvent = useMemo(() => {
    const map = {};
    const todayIso = fromTo.from;
    for (const o of occurrences) {
      if (o.occurrence_date < todayIso) continue;
      if (!map[o.id] || o.occurrence_date < map[o.id].occurrence_date) {
        map[o.id] = o;
      }
    }
    return map;
  }, [occurrences, fromTo.from]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((ev) => filter === "all" || ev.event_type === filter)
      .filter((ev) => !q || ev.title.toLowerCase().includes(q))
      .map((ev) => ({ ev, next: nextByEvent[ev.id] || null }))
      .sort((a, b) => {
        // Sort by next occurrence date asc (events without an upcoming
        // hit sink to the bottom).
        const ad = a.next?.occurrence_date || "9999-12-31";
        const bd = b.next?.occurrence_date || "9999-12-31";
        return ad.localeCompare(bd);
      });
  }, [items, filter, query, nextByEvent]);

  const onDelete = async (id) => {
    if (!window.confirm("למחוק את האירוע?")) return;
    try {
      await api.myEventDelete(id);
      toast.success("האירוע נמחק");
      fetchAll();
    } catch {
      toast.error("שגיאה במחיקה");
    }
  };

  if (!user) {
    return (
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-16 text-center">
        <p className="text-sm text-muted-foreground">
          התחבר כדי לראות את האירועים האישיים שלך.
        </p>
      </main>
    );
  }

  return (
    <main
      className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10"
      data-testid="my-events-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <NavLink
          to="/calendar"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          data-testid="back-to-calendar"
        >
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          חזרה ללוח שנה
        </NavLink>
        <Button
          className="bg-gold/90 hover:bg-gold text-[#1E2A3A]"
          onClick={() => { setEditing(null); setFormOpen(true); }}
          data-testid="add-event-btn"
        >
          <Plus className="h-4 w-4 ml-1" strokeWidth={1.5} />
          הוסף אירוע
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <CalIcon className="h-6 w-6 text-gold" strokeWidth={1.4} />
        <h1
          className="font-heading text-2xl sm:text-3xl text-foreground tracking-wide"
          data-testid="my-events-title"
        >
          האירועים שלי
        </h1>
      </div>

      {/* Controls */}
      <div className="space-y-3 mb-5">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="חיפוש לפי כותרת…"
            dir="auto"
            className="pr-9"
            data-testid="my-events-search"
          />
        </div>

        <div className="flex flex-wrap gap-1.5" data-testid="my-events-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filter === f.id
                  ? "bg-gold/15 text-foreground border-gold/50"
                  : "border-border/60 text-muted-foreground hover:border-gold/30"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="grid place-items-center py-20" data-testid="my-events-loading">
          <Loader2 className="h-5 w-5 animate-spin text-gold" />
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-border/60 p-10 text-center"
          data-testid="my-events-empty"
        >
          <p className="text-sm text-muted-foreground">
            {items.length === 0
              ? "אין אירועים אישיים. הוסף את הראשון."
              : "לא נמצאו תוצאות לסינון הנוכחי."}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="my-events-list">
          {rows.map(({ ev, next }) => {
            const diff = daysFromNow(next?.occurrence_date);
            return (
              <li
                key={ev.id}
                className="rounded-md border border-border/40 bg-card/40 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`my-event-${ev.id}`}
              >
                {/* Color + title */}
                <div className="flex-1 min-w-0 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <span className="font-medium text-foreground truncate">
                      {ev.title}
                    </span>
                    <span
                      className={`inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 ${COLOR_DOT[ev.color] || "bg-gold/80"}`}
                    />
                  </div>
                  <div className="mt-0.5 flex items-center justify-end gap-2 text-[11px] text-muted-foreground flex-wrap">
                    <span className="rounded-sm bg-muted/40 px-1.5 py-0.5">
                      {TYPE_LABEL[ev.event_type] || ev.event_type}
                    </span>
                    <span className="rounded-sm bg-muted/40 px-1.5 py-0.5">
                      {RECURRENCE_LABEL[ev.recurrence] || ev.recurrence}
                    </span>
                    {ev.time && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" strokeWidth={1.5} />
                        {ev.time}
                      </span>
                    )}
                  </div>
                  {next && (
                    <div className="mt-1.5 text-xs text-foreground/90 text-right">
                      <span className="text-muted-foreground">הופעה הקרובה:</span>{" "}
                      <span className="font-medium">{next.occurrence_hebrew}</span>{" "}
                      <span className="text-muted-foreground">·</span>{" "}
                      <span>{next.occurrence_date}</span>
                      <span className="text-gold/90 mr-1">· {relativeLabel(diff)}</span>
                    </div>
                  )}
                  {next?.is_adar_shifted && (
                    <div className="mt-1 text-[11px] text-gold flex items-center justify-end gap-1">
                      <AlertCircle className="h-3 w-3" strokeWidth={1.5} />
                      השנה האירוע יציין באדר ב' (מנהג אשכנז)
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 self-end sm:self-center">
                  <button
                    onClick={() => { setEditing(ev); setFormOpen(true); }}
                    className="p-2 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground"
                    aria-label="ערוך"
                    data-testid={`edit-${ev.id}`}
                  >
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => onDelete(ev.id)}
                    className="p-2 rounded hover:bg-rose-100/40 text-muted-foreground hover:text-rose-600"
                    aria-label="מחק"
                    data-testid={`delete-${ev.id}`}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <EventForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        existing={editing}
        onSaved={fetchAll}
      />
    </main>
  );
}
